import { useContext, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Box, Link, Skeleton, Text, Title } from '@factorialco/deprecated-design-system'
import { EnpsAnalyticsWrapperDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import CycleSelector from 'modules/enps/components/CycleSelector'
import Filters from 'modules/enps/components/Filters'
import useAnalyticsMultiFilters, {
  AnalyticsFilterForm,
} from 'modules/enps/hooks/useAnalyticsMultiFilters'
import { EnpsFormContext } from 'modules/enps/lib/EnpsFormContext'
import 'modules/enps/pages/AnalyticsPage/index.styles.css'
import type Form from 'modules/enps/store/models/Form'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import filterFields from 'modules/surveys/components/Filters/filterFields'

import BlankState from './BlankState'
import Distribution from './Distribution'
import InsightsAI from './InsightsAI'
import ParticipationNumber from './ParticipationNumber'
import ScoreNumber from './ScoreNumber'
import { useAnalytics } from './useAnalytics'

const AnalyticsWrapper = () => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { data, loading: loadingFeatures } = useQuery(EnpsAnalyticsWrapperDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const { form, pathname } = useOutletContext<{ form: Form; pathname: string }>()
  const { analyticResults, answeredResultsCount, cycle, setCycle, loading } = useAnalytics()
  const { setAnalyticsFilter } = useContext(EnpsFormContext)

  const {
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
    localFilter,
  } = useAnalyticsMultiFilters(setAnalyticsFilter)

  const notEnoughAnswers = answeredResultsCount < 3
  const helpCenterUrl = 'https://help.factorialhr.com/enps-what-is-that-and-how-can-i-use-it'

  useEffect(() => {
    if (!loading && !loadingFeatures) {
      track('eNPS analytics view')
    }
  }, [loading, loadingFeatures])

  const [fields, setFields] = useState<readonly MultiFilterOption[]>([])

  const { loading: loadingFilters } = useAsyncData(async () => {
    const fields = await filterFields()

    setFields(fields)
  }, [])

  const canEdit = form && form.policy.can('editEnpsForm')
  const hasEnpsInsights =
    data?.features.featuresConnection.nodes.some(
      (feature) => feature.name === 'dev_enps_insights' && feature.enabled
    ) || false

  const onNavigateToSettings = () => {
    track(trackingEvents.enps.goToEnpsSettings, {
      coming_from: pathname,
      is_launched: form.isActive,
    })

    navigate(navigation.engagement.enps.configuration.fullPath())
  }

  const isLoading = loading || loadingFeatures

  return (
    <ResponsivePage.Body
      showHeader={false}
      actions={{
        primary:
          form && canEdit
            ? [
                {
                  onClick: () => onNavigateToSettings(),
                  icon: 'settings',
                  variant: 'secondary',
                  label: i18n.t('common.settings'),
                } as const,
              ]
            : [],
      }}
    >
      <Box gap="s16">
        <Title
          title={i18n.t('enps.analytics.title')}
          description={i18n.t('enps.analytics.description')}
          illustration="report-library"
          variant="600"
        />
        <Box flexDirection="row" gap="s16">
          {loadingFilters && <Skeleton width="s96" height="s32" />}
          {!loadingFilters && (
            <Filters
              label={i18n.t('filters.multi_filters.title')}
              fields={fields}
              filters={filters}
              logicOperator={logicOperator}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onApplyFilters={(isActive, defaultFilter) => {
                onApplyFilters(isActive, defaultFilter)
                setAnalyticsFilter(
                  new AnalyticsFilterForm(filters, logicOperator.values, defaultFilter, isActive)
                )
                const selectedFilter =
                  defaultFilter && defaultFilter.length > 0 ? defaultFilter[0]?.values['field'] : ''
                if (selectedFilter) {
                  track(trackingEvents.enps.applyFiltersEnpsSubmit, {
                    coming_from: pathname,
                    filter_by: selectedFilter,
                    number_results: answeredResultsCount,
                  })
                }
              }}
              onCancel={onCancel}
              filterKey={localFilter}
            />
          )}
          {form.hasSchedule && (
            <CycleSelector analyticResults={analyticResults} cycle={cycle} setCycle={setCycle} />
          )}
        </Box>

        {isLoading && <Loading />}
        {!isLoading && notEnoughAnswers && <BlankState />}
        {!isLoading && !notEnoughAnswers && (
          <Box gap="s24" paddingTop="s16">
            <Box gap="s24" flexDirection="row" className={'AnalyticsPage_analyticsColumns'}>
              <ScoreNumber />
              <ParticipationNumber />
              <Distribution />
            </Box>
            {hasEnpsInsights && (
              <>
                <Box
                  flexDirection="row"
                  alignItems="stretch"
                  textAlign="center"
                  justifyContent="spaceBetween"
                >
                  <Text.Header2 htmlTag="p">
                    {i18n.t('enps.analytics.people_mentions.title')}
                  </Text.Header2>
                  <Box alignItems="flexEnd" flexDirection="rowReverse">
                    <Link.Router to={navigation.engagement.enps.results.fullPath()}>
                      {i18n.t('enps.analytics.redirection_links.results_link')}
                    </Link.Router>
                  </Box>
                </Box>
                <Box gap="s24" flexDirection="row">
                  <InsightsAI />
                </Box>
                <Box style={{ width: 'fit-content' }}>
                  <Link.Href href={helpCenterUrl} target="_blank">
                    {i18n.t('enps.analytics.redirection_links.calculate_enps_link')}
                  </Link.Href>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(AnalyticsWrapper)
